<script lang="ts">
import { AppVehicleCardGridComponent } from "./app-vehicle-card-grid.ts";
export default AppVehicleCardGridComponent;
</script>

<template>
    <div class="fav__car" v-if="isApproved" v-bind="$attrs">
        <div class="fav__car-top">
            <h5 class="h5 fav__car-name">
                {{ vehicleTileDto.brand?.name + " " + vehicleTileDto.model }}
            </h5>
            <p class="fz-smaller fav__car-model">
                {{ vehicleTileDto.motorisation }} {{ vehicleTileDto.finish }}
            </p>
        </div>
        <div class="fav__car-picture" :class="{ 'arrow-hover': arrowHover }">
            <div class="fav__car-overlay">
                <div
                    v-if="vehicleTileDto.thumbnail_image"
                    class="swiper-container slider__gallery fav__car-slider"
                    v-app-gallery
                >
                    <div class="swiper-wrapper">
                        <div class="swiper-slide dflex">
                            <img
                                class="swiper-lazy"
                                :data-src="vehicleTileDto.thumbnail_image.href_small"
                                :style="{
                                    objectFit: 'cover',
                                    'background-color': '#f4f8fd',
                                }"
                            />
                            <div class="swiper-lazy-preloader"></div>
                        </div>
                    </div>
                </div>
                <router-link
                    :to="{
                        path: `/auth/vehicle-details/${vehicleTileDto.id}`,
                        query: {
                            ...filters,
                        },
                    }"
                    custom
                    v-slot="{ href, navigate }"
                >
                    <a
                        v-app-auth-required="{
                            role: 'dealer',
                            showLogin: !isAuthenticated,
                            disable: true,
                        }"
                        :href="href"
                        class="button button--outline"
                        @click.prevent="() => routeToCarDetails(navigate, true)"
                    >
                        <i18n-t keypath="carCard.viewDetails"></i18n-t>
                    </a>
                </router-link>
            </div>

            <button
                v-if="isApproved"
                class="add-favorite"
                type="button"
                @click="toggleFavorite"
                :class="{ active: vehicleTileDto.is_favorite }"
            >
                <svg class="sprite-icon add-favorite__icon" width="16" height="14">
                    <use xlink:href="/img/sprite.svg#heart-fill"></use>
                </svg>
            </button>
            <div
                class="fav__car-mark"
                v-for="promotion in promotionDtos"
                v-bind:key="promotion"
            >
                <div
                    class="mark__icon"
                    :style="{
                        backgroundImage: `url(/img/our-cars/${iconNameForPromotion(
                            promotion,
                        )}.svg)`,
                    }"
                ></div>
                <span class="mark__text">
                    {{ promotion?.name }}
                </span>
            </div>
        </div>
        <div class="fav__car-bottom">
            <div class="fav__car-price" v-if="vehicleTileDto.pricing">
                <template v-if="vehicleTileDto.pricing?.discount_euro <= 0">
                    <div class="price">
                        <p class="h4">
                            <app-format-currency
                                :value="vehicleTileDto.pricing.list_price_euro"
                                :currency="currency"
                            ></app-format-currency>
                        </p>
                    </div>
                    <div class="price__bottom">
                        <span class="price__bottom-text">
                            <i18n-t keypath="carCard.excludeVAT"></i18n-t>
                        </span>
                    </div>
                </template>
                <div
                    class="price price__discount"
                    v-if="vehicleTileDto.pricing?.discount_euro > 0"
                >
                    <p class="h4">
                        <app-format-currency
                            :value="vehicleTileDto.pricing.price_after_discount_euro"
                            :currency="currency"
                        ></app-format-currency>
                    </p>
                    <div class="price__mark">
                        <img class="price__mark-bg" src="/img/red-mark.png" alt="" />
                        <span class="price__mark--minus">-</span>
                        <span>{{ vehicleTileDto.pricing.discount_percentage }}</span>
                        <span class="price__mark--percent">%</span>
                    </div>
                </div>
                <div
                    class="price__bottom"
                    v-if="vehicleTileDto.pricing.discount_euro > 0"
                >
                    <span
                        class="fz-smaller price__bottom-num"
                        :class="{
                            'line-through': vehicleTileDto.pricing.list_price_euro,
                        }"
                    >
                        <app-format-currency
                            :value="vehicleTileDto.pricing.list_price_euro"
                            :currency="currency"
                        ></app-format-currency>
                    </span>
                    <span class="price__bottom-text">
                        <i18n-t keypath="carCard.excludeVAT"></i18n-t>
                    </span>
                </div>
            </div>
            <div class="fav__car-features">
                <div class="features__list">
                    <template v-if="vehicleTileDto.mileage">
                        <div
                            class="features__list-label tooltip__info"
                            v-tippy="{
                                content: i18n.t('carCard.kmUsed'),
                                interactive: true,
                            }"
                        >
                            <p class="features__list-text">
                                {{ vehicleTileDto.mileage }}
                                <b>KM</b>
                            </p>
                        </div>
                    </template>

                    <app-vehicle-options
                        :vehicle-id="vehicleTileDto.id"
                        :show-label="false"
                        :size="6"
                    />
                </div>
            </div>

            <div class="fav__car-stock">
                <div
                    class="stock__color purple tooltip__info"
                    :class="[`--${vehicleTileDto.color?.name}`]"
                    v-tippy="{
                        content: vehicleTileDto.color?.name,
                        interactive: true,
                    }"
                    :style="{
                        backgroundColor: vehicleTileDto.color?.hex,
                        color: vehicleTileDto.color?.is_light ? 'black' : 'white',
                    }"
                ></div>
            </div>
        </div>
    </div>

    <div class="fav__car not-login" v-else v-bind="$attrs">
        <div class="fav__car-top">
            <h5 class="h5 fav__car-name">
                {{ vehicleTileDto.brand?.name + " " + vehicleTileDto.model }}
            </h5>
            <p class="fz-smaller fav__car-model">
                {{ vehicleTileDto.motorisation }} {{ vehicleTileDto.finish }}
            </p>
            <div class="fav__car-comparison tooltip__info">
                <div class="checkbox">
                    <label>
                        <input type="checkbox" v-model="isSelectedForComparison" />
                        <i>
                            <svg
                                class="sprite-icon comparison__icon"
                                width="20"
                                height="20"
                            >
                                <use xlink:href="/img/sprite.svg#compare"></use>
                            </svg>
                        </i>
                    </label>
                </div>
            </div>
        </div>
        <div class="fav__car-picture" :class="{ 'arrow-hover': arrowHover }">
            <div class="fav__car-overlay">
                <router-link
                    :to="{
                        path: `/auth/vehicle-details/${vehicleTileDto.id}`,
                        query: {
                            ...filters,
                        },
                    }"
                    custom
                    v-slot="{ href, navigate }"
                >
                    <a
                        v-app-auth-required="{
                            role: 'dealer',
                            showLogin: !isAuthenticated,
                            disable: true,
                        }"
                        :href="href"
                        class="button button--outline"
                        @click.prevent="() => routeToCarDetails(navigate, true)"
                    >
                        <i18n-t keypath="carCard.viewDetails"></i18n-t>
                    </a>
                </router-link>
            </div>
            <div
                class="fav__car-mark"
                v-for="promotion in promotionDtos"
                v-bind:key="promotion"
            >
                <div
                    class="mark__icon"
                    :style="{
                        backgroundImage: `url(/img/our-cars/${iconNameForPromotion(
                            promotion,
                        )}.svg)`,
                    }"
                ></div>
                <span class="mark__text">
                    {{ promotion?.name }}
                </span>
            </div>
            <button
                v-if="isApproved"
                class="add-favorite"
                type="button"
                @click="toggleFavorite"
                :class="{ active: vehicleTileDto.is_favorite }"
            >
                <svg class="sprite-icon add-favorite__icon" width="16" height="14">
                    <use xlink:href="/img/sprite.svg#heart-fill"></use>
                </svg>
            </button>
            <img
                class="fav__car__image"
                v-if="vehicleTileDto.thumbnail_image"
                :src="vehicleTileDto.thumbnail_image.href_small"
                :style="{ 'background-color': '#f4f8fd' }"
            />
        </div>
        <div class="fav__car-bottom">
            <div class="fav__car-price" v-if="vehicleTileDto.pricing">
                <template>
                    <div class="price">
                        <p class="h4">
                            <app-format-currency
                                :value="vehicleTileDto.pricing.list_price_euro"
                                :currency="currency"
                            ></app-format-currency>
                        </p>
                    </div>
                    <div class="price__bottom">
                        <span class="price__bottom-text">
                            <i18n-t keypath="carCard.excludeVAT"></i18n-t>
                        </span>
                    </div>
                </template>
                <div
                    class="price price__discount"
                    v-if="vehicleTileDto.pricing.discount_euro > 0"
                >
                    <p class="h4">
                        <app-format-currency
                            :value="vehicleTileDto.pricing.discount_euro"
                            :currency="currency"
                        ></app-format-currency>
                    </p>
                    <div class="price__mark">
                        <img class="price__mark-bg" src="/img/red-mark.png" alt="" />
                        <span class="price__mark--minus">-</span>
                        <span>{{ vehicleTileDto.pricing.discount_percentage }}</span>
                        <span class="price__mark--percent">%</span>
                    </div>
                </div>
                <div
                    class="price__bottom"
                    v-if="vehicleTileDto.pricing.discount_euro > 0"
                >
                    <span
                        class="fz-smaller price__bottom-num"
                        :class="{
                            'line-through': vehicleTileDto.pricing.list_price_euro,
                        }"
                    >
                        <app-format-currency
                            :value="vehicleTileDto.pricing.price_after_discount_euro"
                            :currency="currency"
                        ></app-format-currency>
                    </span>
                    <span class="price__bottom-text">
                        <i18n-t keypath="carCard.excludeVAT"></i18n-t>
                    </span>
                </div>
            </div>
            <div class="fav__car-features">
                <div class="features__list">
                    <template v-if="vehicleTileDto.mileage">
                        <div
                            class="features__list-label tooltip__info"
                            v-tippy="{
                                content: i18n.t('carCard.kmUsed'),
                                interactive: true,
                            }"
                        >
                            <p class="features__list-text">
                                {{ vehicleTileDto.mileage }}
                                <b>KM</b>
                            </p>
                        </div>
                    </template>

                    <app-vehicle-options
                        :vehicle-id="vehicleTileDto.id"
                        :show-label="false"
                        :size="6"
                    />
                </div>
            </div>

            <div class="fav__car-stock">
                <!-- <p
                    class="stock__label"
                    v-html="i18n.t('carCard.inStock', 1)"
                ></p> -->

                <div
                    class="stock__color purple tooltip__info"
                    :class="[`--${vehicleTileDto.color?.name}`]"
                    v-tippy="{
                        content: vehicleTileDto.color?.name,
                        interactive: true,
                    }"
                    :style="{
                        backgroundColor: vehicleTileDto.color?.hex,
                        color: vehicleTileDto.color?.is_light ? 'black' : 'white',
                    }"
                >
                    {{ 1 }}
                </div>
            </div>
        </div>
    </div>
</template>
